import React from 'react';
import { X, Clock, CheckCircle, Package, AlertCircle, XCircle, Calendar, Printer, Pause, Play } from 'lucide-react';

const CustomerOrderHistoryModal = ({ isOpen, onClose, order }) => {
  if (!isOpen || !order) return null;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status, type) => {
    // Bekletme/Devam ettirme için özel bilgiler
    if (type === 'hold') {
      return {
        label: 'Bekletildi',
        icon: Pause,
        color: 'orange',
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        textColor: 'text-orange-800 dark:text-orange-200',
        borderColor: 'border-orange-400'
      };
    }
    if (type === 'resume') {
      return {
        label: 'Devam Ettirildi',
        icon: Play,
        color: 'green',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        textColor: 'text-green-800 dark:text-green-200',
        borderColor: 'border-green-400'
      };
    }

    const statusMap = {
      bekleyen: {
        label: 'Bekleyen',
        icon: Clock,
        color: 'yellow',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        textColor: 'text-yellow-800 dark:text-yellow-200',
        borderColor: 'border-yellow-400'
      },
      termin_verildi: {
        label: 'Termin Verildi',
        icon: Calendar,
        color: 'purple',
        bgColor: 'bg-purple-50 dark:bg-purple-900/20',
        textColor: 'text-purple-800 dark:text-purple-200',
        borderColor: 'border-purple-400'
      },
      dijital_baski: {
        label: 'Dijital Baskı',
        icon: Printer,
        color: 'indigo',
        bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
        textColor: 'text-indigo-800 dark:text-indigo-200',
        borderColor: 'border-indigo-400'
      },
      teslimata_hazir: {
        label: 'Teslimata Hazır',
        icon: Package,
        color: 'green',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        textColor: 'text-green-800 dark:text-green-200',
        borderColor: 'border-green-400'
      },
      tamamlanan: {
        label: 'Tamamlanan',
        icon: CheckCircle,
        color: 'blue',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        textColor: 'text-blue-800 dark:text-blue-200',
        borderColor: 'border-blue-400'
      },
      temin_edilemeyen: {
        label: 'Temin Edilemeyen',
        icon: AlertCircle,
        color: 'orange',
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        textColor: 'text-orange-800 dark:text-orange-200',
        borderColor: 'border-orange-400'
      },
      iptal_edilen: {
        label: 'İptal Edilen',
        icon: XCircle,
        color: 'red',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        textColor: 'text-red-800 dark:text-red-200',
        borderColor: 'border-red-400'
      }
    };
    return statusMap[status] || statusMap.bekleyen;
  };

  // Sipariş geçmişini oluştur
  const historyItems = [];

  // Sipariş oluşturulma
  if (order.createdAt) {
    historyItems.push({
      type: 'created',
      label: 'Sipariş Oluşturuldu',
      date: order.createdAt,
      note: null,
      status: null
    });
  }

  // Bekleyen durumu
  if (order.bekleyenDate) {
    historyItems.push({
      type: 'status',
      label: 'Bekleyen',
      date: order.bekleyenDate,
      note: order.bekleyenNote,
      status: 'bekleyen'
    });
  }

  // Termin verildi durumu
  if (order.terminVerildiDate) {
    historyItems.push({
      type: 'status',
      label: 'Termin Verildi',
      date: order.terminVerildiDate,
      note: order.terminVerildiNote,
      status: 'termin_verildi'
    });
  }

  // Dijital baskı durumu
  if (order.dijitalBaskiDate) {
    historyItems.push({
      type: 'status',
      label: 'Dijital Baskı',
      date: order.dijitalBaskiDate,
      note: order.dijitalBaskiNote,
      status: 'dijital_baski'
    });
  }

  // Teslimata hazır durumu
  if (order.teslimataHazirDate) {
    historyItems.push({
      type: 'status',
      label: 'Teslimata Hazır',
      date: order.teslimataHazirDate,
      note: order.teslimataHazirNote,
      status: 'teslimata_hazir'
    });
  }

  // Tamamlanan durumu
  if (order.tamamlananDate) {
    historyItems.push({
      type: 'status',
      label: 'Tamamlanan',
      date: order.tamamlananDate,
      note: order.tamamlananNote,
      status: 'tamamlanan'
    });
  }

  // Temin edilemeyen durumu
  if (order.teminEdilemeyenDate) {
    historyItems.push({
      type: 'status',
      label: 'Temin Edilemeyen',
      date: order.teminEdilemeyenDate,
      note: order.teminEdilemeyenNote,
      status: 'temin_edilemeyen'
    });
  }

  // İptal edilen durumu
  if (order.iptalEdilenDate) {
    historyItems.push({
      type: 'status',
      label: 'İptal Edilen',
      date: order.iptalEdilenDate,
      note: order.iptalEdilenNote,
      status: 'iptal_edilen'
    });
  }

  // Bekletme/Devam ettirme geçmişi
  if (order.holdHistory && Array.isArray(order.holdHistory)) {
    order.holdHistory.forEach(holdItem => {
      historyItems.push({
        type: holdItem.action === 'hold' ? 'hold' : 'resume',
        label: holdItem.action === 'hold' ? 'Bekletildi' : 'Devam Ettirildi',
        date: holdItem.actionDate,
        note: holdItem.note,
        status: null
      });
    });
  }

  // Tarihe göre sırala (en eski en üstte)
  historyItems.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Sipariş Geçmişi
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {order.stockCardName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {historyItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Henüz geçmiş kaydı bulunmamaktadır.
            </div>
          ) : (
            <div className="space-y-4">
              {historyItems.map((item, index) => {
                const statusInfo = item.status ? getStatusInfo(item.status) : (item.type === 'hold' || item.type === 'resume' ? getStatusInfo(null, item.type) : null);
                const Icon = statusInfo?.icon || Clock;

                return (
                  <div
                    key={index}
                    className={`relative pl-8 pb-4 ${
                      index < historyItems.length - 1 ? 'border-l-2 border-gray-200 dark:border-gray-700' : ''
                    }`}
                  >
                    {/* Timeline dot */}
                    <div
                      className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 ${
                        statusInfo
                          ? `${statusInfo.bgColor} ${statusInfo.borderColor} border-2`
                          : 'bg-gray-200 dark:bg-gray-700 border-gray-400'
                      } flex items-center justify-center`}
                    >
                      <Icon className={`w-2.5 h-2.5 ${statusInfo?.textColor || 'text-gray-600 dark:text-gray-400'}`} />
                    </div>

                    {/* Content */}
                    <div
                      className={`${
                        statusInfo ? statusInfo.bgColor : 'bg-gray-50 dark:bg-gray-700'
                      } rounded-lg p-4`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3
                              className={`font-semibold ${
                                statusInfo?.textColor || 'text-gray-900 dark:text-white'
                              }`}
                            >
                              {item.label}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {formatDate(item.date)}
                          </p>
                          {item.note && item.note.trim() && (
                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                              <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/50 px-3 py-2 rounded-md">
                                <span className="font-semibold text-gray-900 dark:text-white">Not:</span>{' '}
                                <span className="text-gray-800 dark:text-gray-200">{item.note}</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderHistoryModal;
